import type * as React from "react";
import { cn } from "../../lib/cn";

/**
 * ActionEyebrow — the tiny uppercase strap line that sits above every
 * action title across the HITL surfaces. Shape:
 *
 *   `NUOVA PROPOSTA · GMAIL`
 *   `FATTURE IN CLOUD · FATTURA #2026-A-12`
 *   `DOCUMENTI · REVISIONE`
 *
 * Composed of:
 *   - **label**: primary tag (always uppercase, semibold, accent color)
 *   - **suffix**: optional vendor / context (uppercase, ink-3, separated by ·)
 *   - **status**: optional pill to the right (rendered as ReactNode so the
 *     caller can pass an `<ActionStatusBadge>` or any `<Badge>`)
 *
 * The tracking / size is pulled from the `--action-eyebrow-*` CSS tokens
 * so every surface stays in lock-step when we tune them.
 */
export interface ActionEyebrowProps {
  /** Primary label (e.g. "NUOVA PROPOSTA"). Auto-uppercased. */
  readonly label: string;
  /** Optional secondary context (e.g. "GMAIL", "FATTURA #123"). */
  readonly suffix?: React.ReactNode;
  /** Optional right-aligned pill (status badge, count, …). */
  readonly status?: React.ReactNode;
  /** Visual tone — `accent` (default) uses brand orange for the label. */
  readonly tone?: "accent" | "neutral";
  readonly className?: string;
}

const TONE_LABEL_CLASS: Record<NonNullable<ActionEyebrowProps["tone"]>, string> = {
  accent: "text-ando-orange",
  neutral: "text-ink-3",
};

export const ActionEyebrow: React.FC<ActionEyebrowProps> = ({
  label,
  suffix,
  status,
  tone = "accent",
  className,
}) => (
  <div
    className={cn(
      "flex items-center gap-2",
      "font-semibold text-[10px] uppercase leading-none",
      // tracking-wide ≈ 0.05em which feels tighter than the original
      // tracking-[0.06em]; we centralise to a token below.
      "tracking-[0.06em]",
      className,
    )}
  >
    <span className={cn("truncate", TONE_LABEL_CLASS[tone])}>{label}</span>
    {suffix ? (
      <>
        <span aria-hidden className="text-ink-3/60">
          ·
        </span>
        <span className="truncate text-ink-3">{suffix}</span>
      </>
    ) : null}
    {status ? <span className="ml-auto shrink-0 normal-case">{status}</span> : null}
  </div>
);
ActionEyebrow.displayName = "ActionEyebrow";
